// ============================================
// PDF智能阅读助手 - 工具函数库
// 版本: 2.0
// ============================================

import { logger } from '../utils/logger';

// ============================================
// 标注分类体系定义
// ============================================

/**
 * 会话类型枚举
 */
export const SESSION_TYPES = {
  GUIDE: 'guide',      // 导读模式
  QA: 'qa',           // 问答模式
  MANUAL: 'manual'    // 手动标注
};

/**
 * 导读子类型枚举
 */
export const GUIDE_SUB_TYPES = {
  QUICK_SCAN: 'guide.quick_scan',           // 快速浏览
  DEEP_DIVE: 'guide.deep_dive',             // 深入理解
  DEEP_REVIEW: 'guide.deep_review',         // 深度审稿 (Maker-Checker)
  CRITICAL_SUMMARY: 'guide.critical_summary', // 批判性总结
  SOCRATIC_DIALOGUE: 'guide.socratic_dialogue' // 苏格拉底对话
};

/**
 * 问答子类型枚举
 */
export const QA_SUB_TYPES = {
  TEXT: 'qa.text',              // 文本问答
  IMAGE: 'qa.image',            // 图片分析
  CONVERSATION: 'qa.conversation' // 多轮对话
};

/**
 * 手动标注子类型枚举
 */
export const MANUAL_SUB_TYPES = {
  IMPORTANT: 'manual.important',  // 重要
  QUESTION: 'manual.question',    // 疑问
  SUMMARY: 'manual.summary',      // 总结
  NOTE: 'manual.note'             // 笔记
};

/**
 * 会话类型配置（包含显示名称和颜色）
 */
export const SESSION_TYPE_CONFIG = {
  [SESSION_TYPES.GUIDE]: {
    name: '📚 导读记录',
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    description: 'AI辅助的论文导读分析'
  },
  [SESSION_TYPES.QA]: {
    name: '💬 问答记录',
    color: '#4CAF50',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    description: '与AI的问答对话记录'
  },
  [SESSION_TYPES.MANUAL]: {
    name: '📝 手动标注',
    color: '#FF9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    description: '用户手动创建的标注'
  }
};

/**
 * 子类型配置（包含图标、颜色等）
 */
export const SUB_TYPE_CONFIG = {
  // 导读子类型
  [GUIDE_SUB_TYPES.QUICK_SCAN]: {
    name: '🔍 快速浏览',
    shortName: '快速浏览',
    color: '#42A5F5',
    bgColor: 'rgba(66, 165, 245, 0.1)',
    icon: '🔍',
    description: 'AI主动分析概要'
  },
  [GUIDE_SUB_TYPES.DEEP_DIVE]: {
    name: '📖 深入理解',
    shortName: '深入理解',
    color: '#1976D2',
    bgColor: 'rgba(25, 118, 210, 0.1)',
    icon: '📖',
    description: '基于快速浏览的详细讲解'
  },
  [GUIDE_SUB_TYPES.DEEP_REVIEW]: {
    name: '🔬 深度审稿',
    shortName: '深度审稿',
    color: '#E91E63',
    bgColor: 'rgba(233, 30, 99, 0.1)',
    icon: '🔬',
    description: 'Maker-Checker 双重验证分析'
  },
  [GUIDE_SUB_TYPES.CRITICAL_SUMMARY]: {
    name: '🎯 批判性总结',
    shortName: '批判性总结',
    color: '#0D47A1',
    bgColor: 'rgba(13, 71, 161, 0.1)',
    icon: '🎯',
    description: '综合分析整篇论文'
  },
  [GUIDE_SUB_TYPES.SOCRATIC_DIALOGUE]: {
    name: '🧠 苏格拉底对话',
    shortName: '苏格拉底对话',
    color: '#6A1B9A',
    bgColor: 'rgba(106, 27, 154, 0.1)',
    icon: '🧠',
    description: 'AI导师引导的深度思考对话'
  },

  // 问答子类型
  [QA_SUB_TYPES.TEXT]: {
    name: '🔤 文本问答',
    shortName: '文本问答',
    color: '#66BB6A',
    bgColor: 'rgba(102, 187, 106, 0.1)',
    icon: '🔤',
    description: '基于文本内容的问答'
  },
  [QA_SUB_TYPES.IMAGE]: {
    name: '🖼️ 图片分析',
    shortName: '图片分析',
    color: '#26A69A',
    bgColor: 'rgba(38, 166, 154, 0.1)',
    icon: '🖼️',
    description: '图片内容识别和分析'
  },
  [QA_SUB_TYPES.CONVERSATION]: {
    name: '💭 多轮对话',
    shortName: '多轮对话',
    color: '#00897B',
    bgColor: 'rgba(0, 137, 123, 0.1)',
    icon: '💭',
    description: '连续对话记录'
  },
  
  // 手动标注子类型
  [MANUAL_SUB_TYPES.IMPORTANT]: {
    name: '⭐ 重要',
    shortName: '重要',
    color: '#FF5722',
    bgColor: 'rgba(255, 87, 34, 0.1)',
    icon: '⭐',
    highlightColor: 'rgba(255, 193, 7, 0.25)',
    description: '重要内容标记'
  },
  [MANUAL_SUB_TYPES.QUESTION]: {
    name: '❓ 疑问',
    shortName: '疑问',
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    icon: '❓',
    highlightColor: 'rgba(33, 150, 243, 0.15)',
    description: '待解决的问题'
  },
  [MANUAL_SUB_TYPES.SUMMARY]: {
    name: '📋 总结',
    shortName: '总结',
    color: '#4CAF50',
    bgColor: 'rgba(76, 175, 80, 0.1)',
    icon: '📋',
    highlightColor: 'rgba(76, 175, 80, 0.2)',
    description: '内容总结归纳'
  },
  [MANUAL_SUB_TYPES.NOTE]: {
    name: '📄 笔记',
    shortName: '笔记',
    color: '#FF9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
    icon: '📄',
    highlightColor: 'rgba(255, 152, 0, 0.15)',
    description: '一般性笔记'
  },

  // 便利贴类型
  'question': {
    name: '📝 疑问便利贴',
    shortName: '疑问',
    color: '#FBC02D',
    bgColor: 'rgba(251, 192, 45, 0.1)',
    icon: '📝',
    description: '记录疑问'
  },
  'idea': {
    name: '💡 想法便利贴',
    shortName: '想法',
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
    icon: '💡',
    description: '记录想法'
  },
  'important': {
    name: '⚠️ 重点便利贴',
    shortName: '重点',
    color: '#F44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
    icon: '⚠️',
    description: '标记重点'
  }
};

/**
 * 兼容旧版本的标签系统（逐步废弃）
 */
export const ANNOTATION_TAGS = {
  important: { 
    name: '重要', 
    color: '#FF5722', 
    bgColor: 'rgba(255, 87, 34, 0.3)', 
    highlightColor: 'rgba(255, 193, 7, 0.25)' 
  },
  question: { 
    name: '疑问', 
    color: '#2196F3', 
    bgColor: 'rgba(33, 150, 243, 0.3)', 
    highlightColor: 'rgba(33, 150, 243, 0.15)' 
  },
  summary: { 
    name: '总结', 
    color: '#4CAF50', 
    bgColor: 'rgba(76, 175, 80, 0.3)', 
    highlightColor: 'rgba(76, 175, 80, 0.2)' 
  },
  note: { 
    name: '笔记', 
    color: '#FF9800', 
    bgColor: 'rgba(255, 152, 0, 0.3)', 
    highlightColor: 'rgba(255, 152, 0, 0.15)' 
  }
};

// ============================================
// 工具函数
// ============================================

/**
 * 根据旧的tag转换为新的sub_type
 * @param {string} tag - 旧的标签值
 * @returns {string} 新的子类型值
 */
export const convertTagToSubType = (tag) => {
  const mapping = {
    'important': MANUAL_SUB_TYPES.IMPORTANT,
    'question': MANUAL_SUB_TYPES.QUESTION,
    'summary': MANUAL_SUB_TYPES.SUMMARY,
    'note': MANUAL_SUB_TYPES.NOTE
  };
  return mapping[tag] || MANUAL_SUB_TYPES.NOTE;
};

/**
 * 从sub_type提取tag（向后兼容）
 * @param {string} subType - 子类型值
 * @returns {string} 标签值
 */
export const extractTagFromSubType = (subType) => {
  if (!subType) return 'note';
  const parts = subType.split('.');
  return parts.length > 1 ? parts[1] : 'note';
};

/**
 * 获取类型配置
 * @param {string} subType - 子类型
 * @returns {Object} 类型配置对象
 */
export const getTypeConfig = (subType, annotation) => {
  // 便利贴使用noteType字段
  if (annotation && annotation.type === 'sticky_note' && annotation.noteType) {
    return SUB_TYPE_CONFIG[annotation.noteType] || SUB_TYPE_CONFIG['question'];
  }
  return SUB_TYPE_CONFIG[subType] || SUB_TYPE_CONFIG[MANUAL_SUB_TYPES.NOTE];
};

/**
 * 判断是否为导读类型
 * @param {string} sessionType - 会话类型
 * @returns {boolean}
 */
export const isGuideType = (sessionType) => {
  return sessionType === SESSION_TYPES.GUIDE;
};

/**
 * 判断是否为问答类型
 * @param {string} sessionType - 会话类型
 * @returns {boolean}
 */
export const isQAType = (sessionType) => {
  return sessionType === SESSION_TYPES.QA;
};

/**
 * 判断是否为手动标注类型
 * @param {string} sessionType - 会话类型
 * @returns {boolean}
 */
export const isManualType = (sessionType) => {
  return sessionType === SESSION_TYPES.MANUAL;
};

/**
 * 格式化时间戳
 * @param {string} timestamp - ISO时间戳
 * @returns {string} 格式化的时间字符串
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @returns {string} 格式化的大小字符串
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

// ============================================
// PDF指纹生成
// ============================================

/**
 * 生成PDF文件指纹
 * @param {File} file - PDF文件对象
 * @returns {Promise<string>} 文件指纹
 */
export const generatePDFFingerprint = async (file) => {
  // 确定性Fallback方法（基于文件内容，不使用时间戳）
  const generateDeterministicHash = async (file) => {
    try {
      // 读取文件前1KB作为样本
      const buffer = await file.arrayBuffer();
      const sampleSize = Math.min(buffer.byteLength, 1024);
      const sample = new Uint8Array(buffer.slice(0, sampleSize));

      // 计算内容hash（确定性）
      let contentHash = 0;
      for (let i = 0; i < sample.length; i++) {
        contentHash = ((contentHash << 5) - contentHash) + sample[i];
        contentHash = contentHash & contentHash; // Convert to 32bit integer
      }

      // 计算文件名hash
      const nameHash = Array.from(file.name)
        .reduce((h, char) => ((h << 5) - h) + char.charCodeAt(0), 0);

      // 生成指纹：文件大小_文件名hash_内容hash
      return `${file.size}_${(nameHash >>> 0).toString(16).substring(0, 8)}_${(contentHash >>> 0).toString(16)}`;
    } catch (error) {
      console.error('确定性hash生成失败:', error);
      // 最后的fallback：只用文件大小和文件名
      const nameHash = Array.from(file.name)
        .reduce((h, char) => ((h << 5) - h) + char.charCodeAt(0), 0);
      return `${file.size}_${(nameHash >>> 0).toString(16)}`;
    }
  };

  // 检查crypto.subtle是否可用（需要HTTPS或localhost）
  if (!window.crypto || !window.crypto.subtle) {
    console.warn('crypto.subtle不可用（需要HTTPS环境），使用确定性fallback方法');
    return await generateDeterministicHash(file);
  }

  try {
    const buffer = await file.arrayBuffer();
    const sampleSize = Math.min(buffer.byteLength, 1024);
    const sampleBuffer = buffer.slice(0, sampleSize);

    const hashBuffer = await crypto.subtle.digest('SHA-1', sampleBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    const nameHash = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(file.name));
    const nameHashHex = Array.from(new Uint8Array(nameHash))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .substring(0, 8);

    const fingerprint = `${file.size}_${nameHashHex}_${hashHex.substring(0, 16)}`;

    return fingerprint;
  } catch (error) {
    console.error('SHA-1指纹生成失败，使用确定性fallback方法:', error);
    return await generateDeterministicHash(file);
  }
};

// ============================================
// IndexedDB操作
// ============================================

/**
 * 打开IndexedDB数据库
 * @returns {Promise<IDBDatabase>}
 */
const DB_VERSION = 2; // 升级版本以强制重建对象存储

export const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PDFReaderDB', DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB打开失败:', event.target.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      const db = request.result;
      // 验证对象存储是否存在
      if (!db.objectStoreNames.contains('pdfFiles')) {
        console.warn('pdfFiles对象存储不存在，需要重建数据库');
        db.close();
        // 删除并重建数据库
        const deleteRequest = indexedDB.deleteDatabase('PDFReaderDB');
        deleteRequest.onsuccess = () => {
          logger.log('数据库已删除，重新创建');
          openDB().then(resolve).catch(reject);
        };
        deleteRequest.onerror = () => reject(deleteRequest.error);
        return;
      }
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      logger.log('IndexedDB升级中，版本:', event.oldVersion, '->', DB_VERSION);

      // 删除旧的对象存储（如果存在）
      if (db.objectStoreNames.contains('pdfFiles')) {
        try {
          db.deleteObjectStore('pdfFiles');
          logger.log('删除旧的pdfFiles对象存储');
        } catch (e) {
          console.warn('删除对象存储失败:', e);
        }
      }

      // 创建新的对象存储
      db.createObjectStore('pdfFiles', { keyPath: 'fingerprint' });
      logger.log('创建新的pdfFiles对象存储');
    };
  });
};

/**
 * 生成PDF第一页缩略图
 * @param {Blob} pdfBlob - PDF文件blob
 * @returns {Promise<string|null>} - base64格式的缩略图或null
 */
export const generateThumbnail = async (pdfBlob) => {
  try {
    // 动态导入pdfjs以避免循环依赖
    const { pdfjs } = await import('react-pdf');

    // 配置worker路径 - 必须使用https协议，每次都设置确保生效
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

    const arrayBuffer = await pdfBlob.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);

    const viewport = page.getViewport({ scale: 0.5 }); // 缩小到50%
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;

    // 转换为base64，使用JPEG格式压缩
    return canvas.toDataURL('image/jpeg', 0.7); // 70%质量
  } catch (error) {
    console.error('生成缩略图失败:', error);
    return null;
  }
};

/**
 * 保存PDF文件到IndexedDB
 * @param {string} fingerprint - 文件指纹
 * @param {File} file - PDF文件对象
 * @returns {Promise<boolean>}
 */
export const savePDFFile = async (fingerprint, file) => {
  try {
    logger.log('开始保存PDF到IndexedDB...');

    // 生成缩略图
    const thumbnail = await generateThumbnail(file);
    logger.log('缩略图生成完成:', thumbnail ? '成功' : '失败');

    const db = await openDB();
    const transaction = db.transaction(['pdfFiles'], 'readwrite');
    const store = transaction.objectStore('pdfFiles');

    const fileData = {
      fingerprint,
      name: file.name,
      size: file.size,
      blob: file,
      thumbnail: thumbnail, // 保存缩略图
      timestamp: new Date().toISOString()
    };

    await new Promise((resolve, reject) => {
      const request = store.put(fileData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    logger.log('PDF保存到IndexedDB成功');
    return true;
  } catch (error) {
    console.error('保存PDF文件失败:', error);
    return false;
  }
};

/**
 * 从IndexedDB加载PDF文件
 * @param {string} fingerprint - 文件指纹
 * @returns {Promise<Blob|null>}
 */
export const loadPDFFile = async (fingerprint) => {
  try {
    const db = await openDB();
    const transaction = db.transaction(['pdfFiles'], 'readonly');
    const store = transaction.objectStore('pdfFiles');
    
    return new Promise((resolve, reject) => {
      const request = store.get(fingerprint);
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result.blob);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('加载PDF文件失败:', error);
    return null;
  }
};

/**
 * 从IndexedDB删除PDF文件
 * @param {string} fingerprint - 文件指纹
 * @returns {Promise<boolean>}
 */
export const deletePDFFile = async (fingerprint) => {
  try {
    const db = await openDB();
    const transaction = db.transaction(['pdfFiles'], 'readwrite');
    const store = transaction.objectStore('pdfFiles');
    
    await new Promise((resolve, reject) => {
      const request = store.delete(fingerprint);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('删除PDF文件失败:', error);
    return false;
  }
};

// ============================================
// Block 类型定义（与后端 BlockType 对应）
// ============================================

export const BLOCK_TYPES = {
  // 问答块
  QA_NORMAL: 'qa_normal',
  QA_QUICK_SCAN: 'qa_quick_scan',
  QA_DEEP_DIVE: 'qa_deep_dive',
  QA_CRITICAL: 'qa_critical',
  QA_SOCRATIC: 'qa_socratic',
  QA_DEEP_REVIEW: 'qa_deep_review',
  // 内容块
  ANNOTATION: 'annotation',
  STICKY_NOTE: 'sticky_note',
  PDF_SNIPPET: 'pdf_snippet',
  PDF_VIEWER: 'pdf_viewer',
};

/**
 * 将 Block 转换为旧的 Annotation 格式（向后兼容）
 * @param {Object} block - Block 数据
 * @returns {Object} Annotation 格式数据
 */
export const blockToAnnotationFormat = (block) => {
  const content = block.content || {};

  // 推断 session_type 和 sub_type
  let sessionType = SESSION_TYPES.MANUAL;
  let subType = MANUAL_SUB_TYPES.NOTE;

  switch (block.type) {
    case BLOCK_TYPES.QA_QUICK_SCAN:
      sessionType = SESSION_TYPES.GUIDE;
      subType = GUIDE_SUB_TYPES.QUICK_SCAN;
      break;
    case BLOCK_TYPES.QA_DEEP_DIVE:
      sessionType = SESSION_TYPES.GUIDE;
      subType = GUIDE_SUB_TYPES.DEEP_DIVE;
      break;
    case BLOCK_TYPES.QA_CRITICAL:
      sessionType = SESSION_TYPES.GUIDE;
      subType = GUIDE_SUB_TYPES.CRITICAL_SUMMARY;
      break;
    case BLOCK_TYPES.QA_SOCRATIC:
      sessionType = SESSION_TYPES.GUIDE;
      subType = GUIDE_SUB_TYPES.SOCRATIC_DIALOGUE;
      break;
    case BLOCK_TYPES.QA_DEEP_REVIEW:
      sessionType = SESSION_TYPES.GUIDE;
      subType = GUIDE_SUB_TYPES.DEEP_REVIEW;
      break;
    case BLOCK_TYPES.QA_NORMAL:
      sessionType = SESSION_TYPES.QA;
      subType = content.annotation_type === 'image' ? QA_SUB_TYPES.IMAGE : QA_SUB_TYPES.TEXT;
      break;
    case BLOCK_TYPES.ANNOTATION:
      sessionType = SESSION_TYPES.MANUAL;
      subType = content.original_sub_type || MANUAL_SUB_TYPES.NOTE;
      break;
    case BLOCK_TYPES.STICKY_NOTE:
      // 便利贴保持特殊格式
      return {
        id: block.id,
        type: 'sticky_note',
        pdfFingerprint: block.pdf_fingerprint,
        page: block.page,
        noteType: content.note_type || 'question',
        content: content.content || '',
        selectedText: content.selected_text || '',
        position: { x: block.position_x, y: block.position_y },
        timestamp: block.created_at,
        updatedAt: block.updated_at,
      };
    default:
      break;
  }

  return {
    id: block.id,
    pdfFingerprint: block.pdf_fingerprint,
    page: block.page,
    text: content.text || content.selected_text || '',
    question: content.question || '',
    answer: content.answer || content.analysis || '',
    tag: (block.tags && block.tags[0]) || 'note',
    annotationType: content.annotation_type || 'text',
    position: {
      x: block.position_x,
      y: block.position_y,
      width: block.width,
      height: block.height,
    },
    imageData: content.image_data,
    imageFormat: content.image_format,
    highlightRects: content.highlight_rects,
    hasHighlight: !!content.highlight_rects,
    sessionType: sessionType,
    subType: subType,
    sessionName: content.session_name,
    conversationHistory: content.conversation_history,
    aiQuickAnalysis: content.analysis,
    aiDeepAnalysis: content.analysis,
    timestamp: block.created_at,
    updatedAt: block.updated_at,
    readingSessionId: block.session_id,
    // 保留原始 block 数据以便需要时使用
    _block: block,
  };
};

/**
 * 将 Annotation 转换为 Block 格式（用于创建新 block）
 * @param {Object} annotation - Annotation 数据
 * @returns {Object} Block 创建请求数据
 */
export const annotationToBlockFormat = (annotation) => {
  // 推断 block type
  let blockType = BLOCK_TYPES.ANNOTATION;

  if (annotation.type === 'sticky_note') {
    blockType = BLOCK_TYPES.STICKY_NOTE;
  } else if (annotation.subType) {
    switch (annotation.subType) {
      case GUIDE_SUB_TYPES.QUICK_SCAN:
        blockType = BLOCK_TYPES.QA_QUICK_SCAN;
        break;
      case GUIDE_SUB_TYPES.DEEP_DIVE:
        blockType = BLOCK_TYPES.QA_DEEP_DIVE;
        break;
      case GUIDE_SUB_TYPES.CRITICAL_SUMMARY:
        blockType = BLOCK_TYPES.QA_CRITICAL;
        break;
      case GUIDE_SUB_TYPES.SOCRATIC_DIALOGUE:
        blockType = BLOCK_TYPES.QA_SOCRATIC;
        break;
      case GUIDE_SUB_TYPES.DEEP_REVIEW:
        blockType = BLOCK_TYPES.QA_DEEP_REVIEW;
        break;
      default:
        if (annotation.sessionType === SESSION_TYPES.QA) {
          blockType = BLOCK_TYPES.QA_NORMAL;
        }
    }
  }

  return {
    type: blockType,
    session_id: annotation.readingSessionId,
    content: {
      question: annotation.question,
      answer: annotation.answer,
      text: annotation.text,
      selected_text: annotation.text,
      analysis: annotation.aiQuickAnalysis || annotation.aiDeepAnalysis,
      highlight_rects: annotation.highlightRects,
      image_data: annotation.imageData,
      image_format: annotation.imageFormat,
      annotation_type: annotation.annotationType,
      conversation_history: annotation.conversationHistory,
      session_name: annotation.sessionName,
    },
    position: annotation.position ? { x: annotation.position.x, y: annotation.position.y } : undefined,
    size: annotation.position ? { width: annotation.position.width || 300, height: annotation.position.height || 200 } : undefined,
    tags: annotation.tag ? [annotation.tag] : [],
    pdf_fingerprint: annotation.pdfFingerprint,
    page: annotation.page,
  };
};

// ============================================
// API调用函数
// ============================================

/**
 * 获取认证 headers
 * @returns {Object} headers 对象
 */
const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

/**
 * 保存PDF信息到服务器
 * @param {string} fingerprint - 文件指纹
 * @param {Object} fileInfo - 文件信息
 * @param {Object} pdfDocInfo - PDF文档信息
 * @returns {Promise<boolean>}
 */
export const savePDFInfo = async (fingerprint, fileInfo, pdfDocInfo) => {
  try {
    const response = await fetch('/api/save-pdf-info', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fingerprint,
        fileName: fileInfo.name,
        fileSize: fileInfo.size,
        totalPages: pdfDocInfo.numPages
      }),
    });

    // 401时交给统一登录
    if (response.status === 401) {
      window.location.href = `/api/auth/oidc/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return false;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error('保存PDF信息失败:', error);
    return false;
  }
};

/**
 * 根据指纹加载标注（使用新的 blocks API）
 * @param {string} fingerprint - 文件指纹
 * @param {string|null} sessionId - 会话ID（可选）
 *   - null/undefined: 加载所有（管理界面）
 *   - 具体ID: 只加载该会话的标注
 * @returns {Promise<Object>}
 */
export const loadAnnotationsByFingerprint = async (fingerprint, sessionId = null) => {
  try {
    // 构建 URL
    let url = `/api/blocks/pdf/${encodeURIComponent(fingerprint)}`;
    const params = new URLSearchParams();
    if (sessionId && sessionId !== 'all') {
      params.append('session_id', sessionId);
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`加载标注失败: ${response.status}`);
    }

    const result = await response.json();

    // 转换 blocks 为 annotations 格式
    const annotations = {};
    if (result.blocks && Array.isArray(result.blocks)) {
      logger.log('[loadAnnotationsByFingerprint] API返回blocks:', result.blocks.length, '个');

      // 🔍 统计原始 block.type 分布
      const rawTypeStats = result.blocks.reduce((acc, b) => {
        acc[b.type || 'undefined'] = (acc[b.type || 'undefined'] || 0) + 1;
        return acc;
      }, {});
      logger.log('[loadAnnotationsByFingerprint] 原始block.type分布:', rawTypeStats);

      result.blocks.forEach(block => {
        const annotation = blockToAnnotationFormat(block);
        // 🔧 标记为已保存，防止 saveAnnotationsToStorage 重复创建
        annotation._saved = true;
        annotations[annotation.id] = annotation;
      });

      // 🔍 统计转换后的 subType 分布
      const subTypeStats = Object.values(annotations).reduce((acc, ann) => {
        acc[ann.subType || 'undefined'] = (acc[ann.subType || 'undefined'] || 0) + 1;
        return acc;
      }, {});
      logger.log('[loadAnnotationsByFingerprint] 转换后subType分布:', subTypeStats);

      logger.log('[loadAnnotationsByFingerprint] 转换后annotations:', {
        count: Object.keys(annotations).length,
        byType: Object.values(annotations).reduce((acc, ann) => {
          acc[ann.sessionType || 'undefined'] = (acc[ann.sessionType || 'undefined'] || 0) + 1;
          return acc;
        }, {})
      });
    } else {
      logger.log('[loadAnnotationsByFingerprint] 无blocks或非数组:', result);
    }

    return { annotations, count: Object.keys(annotations).length };
  } catch (error) {
    console.error('加载标注失败:', error);
    return { annotations: {}, count: 0 };
  }
};

/**
 * 根据指纹加载已删除的标注（使用新的 blocks API）
 * @param {string} fingerprint - 文件指纹
 * @returns {Promise<Object>}
 */
export const loadDeletedAnnotationsByFingerprint = async (fingerprint) => {
  try {
    const response = await fetch(`/api/blocks/pdf/${encodeURIComponent(fingerprint)}/deleted`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    // 转换 blocks 为 annotations 格式
    const annotations = {};
    if (result.blocks && Array.isArray(result.blocks)) {
      result.blocks.forEach(block => {
        const annotation = blockToAnnotationFormat(block);
        annotations[annotation.id] = annotation;
      });
    }

    return { annotations, count: Object.keys(annotations).length };
  } catch (error) {
    console.error('加载已删除标注失败:', error);
    return { annotations: {}, count: 0 };
  }
};

/**
 * 保存标注到服务器（使用新的 blocks API）
 * @param {string} fingerprint - 文件指纹
 * @param {Object} annotations - 标注对象
 * @returns {Promise<boolean>}
 */
export const saveAnnotationsToStorage = async (fingerprint, annotations) => {
  if (!fingerprint) return false;

  try {
    // 🔧 修复：只保存新增/更新的标注，不要为所有标注创建 block
    // 找出需要保存的标注（没有 _saved 标记的）
    const annotationsToSave = Object.values(annotations).filter(a => !a._saved);

    if (annotationsToSave.length === 0) {
      logger.log('没有新标注需要保存');
      return true;
    }

    logger.log(`准备保存 ${annotationsToSave.length} 个新标注（总共 ${Object.keys(annotations).length} 个）`);

    const savePromises = annotationsToSave.map(async annotation => {
      // 转换为 block 格式
      const blockData = annotationToBlockFormat(annotation);
      blockData.content.fingerprint = fingerprint;

      const response = await fetch('/api/blocks/', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(blockData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // 标记为已保存
      annotation._saved = true;

      return response.json();
    });

    await Promise.all(savePromises);
    return true;
  } catch (error) {
    console.error('保存标注失败:', error);
    return false;
  }
};

/**
 * 获取PDF历史记录
 * @returns {Promise<Array>}
 */
export const getPDFHistory = async () => {
  try {
    const response = await fetch('/api/pdf-history');
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    return result.history.map(item => ({
      fingerprint: item.fingerprint,
      fileName: item.file_name,
      fileSize: item.file_size,
      totalPages: item.total_pages,
      lastAccessed: item.last_accessed,
      uploadTime: item.upload_time,
      annotationsCount: item.annotations_count || 0
    }));
  } catch (error) {
    console.error('获取PDF历史失败:', error);
    return [];
  }
};

/**
 * 清空PDF历史记录
 * @returns {Promise<boolean>}
 */
export const clearPDFHistory = async () => {
  try {
    const response = await fetch('/api/clear-history', {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      const history = await getPDFHistory();
      for (const item of history) {
        await deletePDFFile(item.fingerprint);
      }
    }
    
    return result.success;
  } catch (error) {
    console.error('清空PDF历史失败:', error);
    return false;
  }
};

/**
 * 从历史记录中删除PDF
 * @param {string} fingerprint - 文件指纹
 * @returns {Promise<boolean>}
 */
export const removePDFFromHistory = async (fingerprint) => {
  try {
    const response = await fetch('/api/remove-pdf', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fingerprint }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      await deletePDFFile(fingerprint);
    }
    
    return result.success;
  } catch (error) {
    console.error('删除PDF失败:', error);
    return false;
  }
};

/**
 * 删除单个标注（使用新的 blocks API）
 * @param {string} annotationId - 标注/Block ID
 * @returns {Promise<boolean>}
 */
export const deleteAnnotationFromStorage = async (annotationId) => {
  try {
    const response = await fetch(`/api/blocks/${annotationId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error('删除标注失败:', error);
    return false;
  }
};

/**
 * 批量删除标注（使用新的 blocks API）
 * @param {Array<string>} annotationIds - 标注/Block ID 列表
 * @param {boolean} permanent - 是否永久删除
 * @returns {Promise<Object>}
 */
export const deleteAnnotationsBatch = async (annotationIds, permanent = false) => {
  try {
    const response = await fetch(`/api/blocks/batch?permanent=${permanent}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify(annotationIds),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('批量删除标注失败:', error);
    return { success: false, deleted: 0, failed: annotationIds.length };
  }
};

/**
 * 批量恢复已删除的标注（使用新的 blocks API）
 * @param {Array<string>} annotationIds - 标注/Block ID 列表
 * @returns {Promise<Object>}
 */
export const restoreAnnotationsBatch = async (annotationIds) => {
  try {
    const response = await fetch('/api/blocks/batch/restore', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(annotationIds),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('批量恢复标注失败:', error);
    return { success: false, restored: 0, failed: annotationIds.length };
  }
};

// ============================================
// 新增：标注搜索（目前通过前端过滤实现）
// ============================================

/**
 * 搜索标注（在已加载的数据中搜索）
 * @param {Object} searchParams - 搜索参数
 * @returns {Promise<Object>}
 */
export const searchAnnotations = async (searchParams) => {
  try {
    const { fingerprint, query, sessionType, page } = searchParams;

    // 加载所有数据
    const { annotations } = await loadAnnotationsByFingerprint(fingerprint);

    // 前端过滤
    let results = Object.values(annotations);

    if (query) {
      const lowerQuery = query.toLowerCase();
      results = results.filter(ann =>
        (ann.question && ann.question.toLowerCase().includes(lowerQuery)) ||
        (ann.answer && ann.answer.toLowerCase().includes(lowerQuery)) ||
        (ann.text && ann.text.toLowerCase().includes(lowerQuery))
      );
    }

    if (sessionType) {
      results = results.filter(ann => ann.sessionType === sessionType);
    }

    if (page !== undefined && page !== null) {
      results = results.filter(ann => ann.page === page);
    }

    return { success: true, results, total: results.length };
  } catch (error) {
    console.error('搜索标注失败:', error);
    return { success: false, results: [], total: 0, error: error.message };
  }
};

// ============================================
// 新增：获取标注统计
// ============================================

/**
 * 获取标注统计信息（使用新的 blocks API）
 * @param {string} fingerprint - 文件指纹
 * @returns {Promise<Object>}
 */
export const getAnnotationStatistics = async (fingerprint) => {
  try {
    const response = await fetch(`/api/blocks/pdf/${encodeURIComponent(fingerprint)}/statistics`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return { success: true, ...result.statistics };
  } catch (error) {
    console.error('获取统计失败:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// 新增：导出标注
// ============================================

/**
 * 导出标注（使用新的 blocks API）
 * @param {Object} exportParams - 导出参数
 * @param {string} exportParams.fingerprint - PDF 指纹
 * @param {string} exportParams.format - 导出格式 (markdown/json/csv)
 * @param {string[]} exportParams.blockTypes - 要导出的块类型
 * @param {boolean} exportParams.includeDeleted - 是否包含已删除的
 * @returns {Promise<Object>}
 */
export const exportAnnotations = async (exportParams) => {
  try {
    const { fingerprint, format = 'markdown', blockTypes, includeDeleted = false } = exportParams;

    const response = await fetch('/api/blocks/export', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        fingerprint,
        format,
        block_types: blockTypes,
        include_deleted: includeDeleted
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('导出标注失败:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 下载导出的内容
 * @param {string} content - 导出内容
 * @param {string} filename - 文件名
 * @param {string} mimeType - MIME类型
 */
export const downloadExportedContent = (content, filename, mimeType = 'text/markdown') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ============================================
// 位置和高亮相关
// ============================================

/**
 * 获取选中文本相对于页面元素的位置信息
 * @param {HTMLElement} pageElement - PDF页面元素
 * @returns {Object|null} 位置信息对象
 */
export const getSelectionPosition = (pageElement) => {
  const selection = window.getSelection();
  if (selection.rangeCount === 0 || !pageElement) return null;

  const range = selection.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  const pageRect = pageElement.getBoundingClientRect();
  
  const relativeX = rect.left - pageRect.left + rect.width / 2;
  const relativeY = rect.top - pageRect.top;
  
  logger.log('🔍 文本选择位置:', {
    selectionRect: rect,
    pageRect: pageRect,
    计算位置: { x: relativeX, y: relativeY },
    文本: selection.toString().trim().substring(0, 30)
  });
  
  return {
    x: Math.max(30, Math.min(relativeX, pageRect.width - 30)),
    y: Math.max(30, relativeY),
    width: rect.width,
    height: rect.height,
    text: selection.toString().trim()
  };
};

/**
 * 通过文本内容查找位置（备选方案）
 * @param {HTMLElement} pageElement - PDF页面元素
 * @param {string} searchText - 搜索文本
 * @param {number} currentScale - 当前缩放比例
 * @returns {Object|null} 位置信息对象
 */
// eslint-disable-next-line no-unused-vars
const findTextPosition = (pageElement, searchText, currentScale = 1) => {
  const textLayer = pageElement.querySelector('.react-pdf__Page__textContent');
  if (!textLayer) return null;

  const spans = textLayer.querySelectorAll('span');
  let foundSpan = null;
  
  for (const span of spans) {
    if (span.textContent.includes(searchText.substring(0, 20))) {
      foundSpan = span;
      break;
    }
  }
  
  if (foundSpan) {
    const rect = foundSpan.getBoundingClientRect();
    const pageRect = pageElement.getBoundingClientRect();
    
    return {
      x: (rect.left - pageRect.left) + rect.width / 2,
      y: rect.top - pageRect.top,
      width: rect.width,
      height: rect.height
    };
  }
  
  return null;
};

// ============================================
// 导出所有功能
// ============================================

const pdfUtils = {
  // 类型定义
  SESSION_TYPES,
  GUIDE_SUB_TYPES,
  QA_SUB_TYPES,
  MANUAL_SUB_TYPES,
  SESSION_TYPE_CONFIG,
  SUB_TYPE_CONFIG,
  ANNOTATION_TAGS,
  BLOCK_TYPES,

  // 工具函数
  convertTagToSubType,
  extractTagFromSubType,
  getTypeConfig,
  isGuideType,
  isQAType,
  isManualType,
  formatTimestamp,
  formatFileSize,

  // Block 转换函数
  blockToAnnotationFormat,
  annotationToBlockFormat,

  // PDF相关
  generatePDFFingerprint,
  savePDFFile,
  loadPDFFile,
  deletePDFFile,

  // API调用
  savePDFInfo,
  loadAnnotationsByFingerprint,
  loadDeletedAnnotationsByFingerprint,
  saveAnnotationsToStorage,
  getPDFHistory,
  clearPDFHistory,
  removePDFFromHistory,
  deleteAnnotationFromStorage,

  // 批量操作（新增）
  deleteAnnotationsBatch,
  restoreAnnotationsBatch,

  // 新增功能
  searchAnnotations,
  getAnnotationStatistics,
  exportAnnotations,
  downloadExportedContent,

  // 位置相关
  getSelectionPosition
};

export default pdfUtils;
